'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_USER_KEY } from '@/lib/auth/common/useSession';
import {
  SESSION_SUSPECT_EVENT,
  USER_DATA_UPDATED_EVENT,
  USER_LOGGED_OUT_EVENT,
} from '@/lib/constants/auth-events';

/**
 * Owns the app's auth-event subscriptions on behalf of every useAuthUser() caller (mounted
 * once, renders nothing) — if each consumer listened itself, one event would fire N
 * invalidations and N /api/auth/session requests instead of one.
 */
export function SessionHydrator() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Login/profile change: only the server session knows the new identity, so re-read it.
    const onUserDataUpdated = () => queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    // Logout: already know the answer is "nobody" — skip the round-trip.
    const onUserLoggedOut = () => queryClient.setQueryData(AUTH_USER_KEY, null);
    // A request 401'd (axios-handler.ts) — invalidate, don't setQueryData(null), since a
    // failing request shouldn't get to declare anyone logged out. `cancelRefetch: false`
    // makes a burst of 401s cost one probe instead of cancelling/restarting per failure.
    const onSessionSuspect = () =>
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY }, { cancelRefetch: false });
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'user') onUserDataUpdated();
    };
    window.addEventListener(USER_DATA_UPDATED_EVENT, onUserDataUpdated);
    window.addEventListener(USER_LOGGED_OUT_EVENT, onUserLoggedOut);
    window.addEventListener(SESSION_SUSPECT_EVENT, onSessionSuspect);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(USER_DATA_UPDATED_EVENT, onUserDataUpdated);
      window.removeEventListener(USER_LOGGED_OUT_EVENT, onUserLoggedOut);
      window.removeEventListener(SESSION_SUSPECT_EVENT, onSessionSuspect);
      window.removeEventListener('storage', onStorage);
    };
  }, [queryClient]);

  return null;
}
